import "../styling/PaymentMethodsStyles.css";
import { devLog } from "../../../utils/devLog";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useStyles } from "../../../context/StyleContext";
import { useTranslation } from "../../../hooks/useTranslation";
import { useCheckout } from "../../../context/CheckoutContext";
import { usePagbrasilCard } from "../../../hooks/usePagbrasilCard";
import {
  mapStylesToCardInputs,
  mapStylesToLabels,
  getCardBrandFromNumber,
  formatExpirationDate,
  normalizeBrazilianStateCode,
} from "../../../utils/pagbrasilStyleMapper";
import { validateBrazilianDocument } from "../../../utils/validationUtils";
import { toast } from "react-toastify";
import { fetchCheckoutDetails } from "../../../api/api";
import { fetchSession, updateSession } from "../../../utils/paymentSession";
import { fetchGatewayConnection, processPayment } from "../../../api/api";
import { formatPaymentError } from "../../../utils/formatPaymentError";
import { handleCitPaymentApiResponse, applyCitDeclineOrErrorUi, CIT_OUTCOME } from "../../../utils/paymentOutcome";
import { safeRedirect } from "../../../utils/redirectUrlBuilder";
import PagbrasilCardFormView from "./PagbrasilCardFormView";
import useWebSocket from "../../../hooks/useWebSocket";

/**
 * PagBrasil Card Payment Form Component
 * 
 * Implements PCI-compliant card payments via PagBrasil.JS SDK.
 * Card details are collected securely and tokenized before being sent to backend.
 * 
 * Features:
 * - Secure card input via PagBrasil.JS
 * - PCI-DSS SAQ A compliance
 * - Real-time card brand detection
 * - Style customization matching merchant theme
 * - Support for installments (1-12)
 * - CPF/CNPJ validation for Brazil
 * - Both hosted and embedded (Altruon) checkout modes
 */

const PagbrasilCardForm = ({
  formData,
  itemDetails,
  billingPlatformId,
  routingEntries,
  planId,
  platformItemDetails,
  prefetchedGatewayConnection,
  checkoutPageConfDto,
  checkoutProcessDto,

  // Props for Altruon/embedded variant
  onCardDataChange,
  onPaymentSuccess,
  isEmbeddedCheckout = false,
  onParentCallback,
  sessionId,
  tenantId,
}) => {
  // Form state
  const [cardholderName, setCardholderName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");
  const [installments, setInstallments] = useState(1);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [documentError, setDocumentError] = useState("");
  const [gatewayConnection, setGatewayConnection] = useState(null);
  const [cardBrand, setCardBrand] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  // const [transactionStatus, setTransactionStatus] = useState("");
  const [checkoutConfDto, setcheckoutConfDto] = useState(
    checkoutPageConfDto || null
  );
  const [showProcessingModal, setProcessingModal] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [hostedPageRedirect, setHostedPageRedirect] = useState(null);
  const isFetchingGateway = useRef(false);

  // Hooks
  const { t } = useTranslation();
  const { styles } = useStyles();
  const {
    triggerPaymentMethodEvent,
    resetTrigger,
    clientIp,
    paymentMethodName,
    platformName,
    setTransactionStatus,
    transactionStatus,
    isHostedPageMode,
  } = useCheckout();

  // Determine sandbox mode from gateway connection
  const isSandbox = gatewayConnection?.credentials?.environment === "sandbox" ||
    gatewayConnection?.credentials?.url?.includes("sandbox") ||
    gatewayConnection?.credentials?.url?.includes("homolog") ||
    false;

  // Find Pagbrasil card routing entry
  const cardPagbrasilRouting = useMemo(() => {
    return routingEntries?.find(
      (entry) => entry.paymentMethod === "card" && entry.platform === "pagbrasil"
    );
  }, [routingEntries]);

  const siteName = useMemo(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const fromQuery = queryParams.get("siteName");
    const fromTenant = tenantId || queryParams.get("tenantId");
    const fromHost = window.location.hostname.split(".")[0];
    return (fromQuery || fromTenant || fromHost || "").toUpperCase();
  }, [tenantId]);

  useEffect(() => {
    if (formData?.document && !documentNumber) {
      setDocumentNumber(String(formData.document).toUpperCase());
    }
  }, [formData?.document, documentNumber]);

  // Initialize PagBrasil SDK
  const {
    ready: sdkReady,
    loading: sdkLoading,
    error: sdkError,
    setCardData,
    setCustomerData,
    setPaymentConfig,
    setOrderData,
    tokenize,
    clearError: clearSdkError,
    initializeWithToken
  } = usePagbrasilCard({
    token: gatewayConnection?.credentials?.token,
    oauthToken: gatewayConnection?.credentials?.oauth_token,
    isSandbox,
    onReady: () => {
      devLog("PagBrasil Card SDK ready", expirationDate );
    },
    onError: (err) => {
      console.error("PagBrasil SDK error:", err);
      setTransactionStatus("fail")
      setError(err);
    },
    onTokenSuccess: (result) => {
      devLog("Card tokenized:", result);
    },
  });

  // Mapped styles
  const inputStyles = useMemo(() => mapStylesToCardInputs(styles), [styles]);
  const labelStyles = useMemo(() => mapStylesToLabels(styles), [styles]);

  // Prefer page-level conf; only fetch when the prop was not provided.
  useEffect(() => {
    if (checkoutPageConfDto) {
      setcheckoutConfDto(checkoutPageConfDto);
      return;
    }
    if (billingPlatformId && planId) {
      loadCheckoutDetails();
    }
  }, [billingPlatformId, planId, checkoutPageConfDto]);

  const loadCheckoutDetails = async () => {
    try {
      const data = await fetchCheckoutDetails(billingPlatformId, planId);
      if (data?.checkoutPageConfDto) {
        setcheckoutConfDto(data.checkoutPageConfDto);
      }
    } catch (error) {
      toast.error(t("messages.error.somethingWrong") || "Something went wrong.");
    }
  };


  // Form handlers
  const handleCardholderNameChange = useCallback((e) => {
    const value = e.target.value;
    setCardholderName(value);
    if (error) setError("");
    onCardDataChange?.({ cardholderName: value });
  }, [error, onCardDataChange]);

  const handleCardNumberChange = useCallback((e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 16);
    setCardNumber(value);
    setCardBrand(getCardBrandFromNumber(value));
    if (error) setError("");
    onCardDataChange?.({ cardNumber: value });
  }, [error, onCardDataChange]);

  const handleExpirationDateChange = useCallback((e) => {
    const value = formatExpirationDate(e.target.value);
    setExpirationDate(value);
    if (error) setError("");
    onCardDataChange?.({ expirationDate: value });
  }, [error, onCardDataChange]);

  const handleCvvChange = useCallback((e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setCvv(value);
    if (error) setError("");
    onCardDataChange?.({ cvv: value });
  }, [error, onCardDataChange]);

  const handleDocumentChange = useCallback((e) => {
    const value = e.target.value.toUpperCase().replace(/[^0-9]/g, '');
    setDocumentNumber(value);
    setDocumentError("");
    onCardDataChange?.({ documentNumber: value });
  }, [onCardDataChange]);

  const handleInstallmentsChange = useCallback((e) => {
    const value = parseInt(e.target.value, 10);
    setInstallments(value);
    onCardDataChange?.({ installments: value });
  }, [onCardDataChange]);

  // Validation
  const validateInputs = useCallback(() => {
    if (!cardholderName || cardholderName.trim().length < 3) {
      setError(t("paymentForms.card.invalidName"));
      return false;
    }
    if (!cardNumber || cardNumber.length < 13) {
      setError(t("paymentForms.pagbrasilCard.invalidCardNumber") || "Invalid card number");
      return false;
    }
    if (!expirationDate || expirationDate.length < 5) {
      setError(t("paymentForms.pagbrasilCard.invalidExpiration") || "Invalid expiration date");
      return false;
    }
    if (!cvv || cvv.length < 3) {
      setError(t("paymentForms.pagbrasilCard.invalidCvv") || "Invalid CVV");
      return false;
    }
    if (!validateBrazilianDocument(documentNumber)) {
      setDocumentError(t("paymentForms.pix.invalidDocument"));
      return false;
    }

    if (typeof window.$PB === 'undefined') {
      setError(t("paymentForms.card.cardNotReady") || "Payment form not ready");
      return false;
    }
    return true;
  }, [cardholderName, cardNumber, expirationDate, cvv, documentNumber, sdkReady, t]);

  // Build payment payload
  const buildPayload = useCallback((cardToken, gateway_connection, latestSession) => {
    const billingState = formData?.billingAddress?.state || 
      latestSession?.customerData?.billingAddress?.state || "SP";

    return {
      paymentData: {
        gatewayConnectionId: gateway_connection?.id,
        gatewayName: gateway_connection?.platform,
        paymentMethod: "card",
        transactionId: "",
        additionalData: {
          payment_method_flow: "DIRECT",
          cardToken: cardToken,
          cardholder_name: cardholderName.trim(),
          card_brand: cardBrand,
          cc_installments: installments,
          document: documentNumber,
          state: billingState,
        },
      },
      customerData: {
        firstName: formData?.firstName || latestSession?.customerData?.firstName,
        lastName: formData?.lastName || latestSession?.customerData?.lastName,
        email: formData?.email || latestSession?.customerData?.email,
        phone: formData?.phone || latestSession?.customerData?.phone || "+5511999999999",
        ip: clientIp || "192.0.126.111",
        billingAddress: {
          country: formData?.billingAddress?.country || latestSession?.customerData?.billingAddress?.country || "BR",
          zipCode: formData?.billingAddress?.zipCode || latestSession?.customerData?.billingAddress?.zipCode || "",
          street: formData?.billingAddress?.street || latestSession?.customerData?.billingAddress?.street || "",
          city: formData?.billingAddress?.city || latestSession?.customerData?.billingAddress?.city || "",
          state: billingState,
          number: "1234",
        },
      },
      billingData: {
        planId: platformItemDetails?.[0]?.id || planId,
        billingPlatformId: billingPlatformId,
        billingPlatformName: itemDetails?.billingPlatformName || latestSession?.billingData?.billingPlatformName,
        frequency: latestSession?.billingData?.frequency || "",
      },
    };
  }, [
    formData, cardholderName, cardBrand, installments, documentNumber,
    clientIp, platformItemDetails, planId, billingPlatformId, itemDetails
  ]);

  // Handle payment processing
  const handleProcessPayment = useCallback(async (payload, cardToken) => {
    if (isEmbeddedCheckout) {
      onParentCallback?.({
        type: "finalizeFormData",
        data: {
          paymentMethod: "card",
          gatewayName: "pagbrasil",
          cardToken: cardToken,
          cardholderName: cardholderName.trim(),
          cardBrand: cardBrand,
          installments: installments,
          documentNumber: documentNumber,
        },
      });
      await new Promise((r) => setTimeout(r, 800));
    }

    if (!isEmbeddedCheckout) {
      await updateSession(sessionId, payload);
    }

    return processPayment(billingPlatformId, sessionId, payload);
  }, [
    isEmbeddedCheckout, onParentCallback, cardholderName, cardBrand,
    installments, documentNumber, sessionId, billingPlatformId
  ]);

  // Main payment handler
  const handlePayNow = useCallback(async () => {
    if (!validateInputs()) {
      return false;
    }
    
    if (!cardPagbrasilRouting) {
      setError(t("paymentForms.card.unavailable"));
      return false; 
    }
    if (!isEmbeddedCheckout) setProcessingModal(true);

    setLoading(true);
    setError("");
    setDocumentError(""); 
    clearSdkError();

    try {
      // Step 1: Fetch gateway connection at pay-time
      const freshGatewayConnection = prefetchedGatewayConnection ||
        await fetchGatewayConnection(cardPagbrasilRouting, t);

      const isSandboxEnv = 
        freshGatewayConnection?.credentials?.environment === "sandbox" ||
        freshGatewayConnection?.credentials?.url?.includes("sandbox") ||
        freshGatewayConnection?.credentials?.url?.includes("homolog") ||
        false;

      // Step 2: Initialize SDK now with fresh token
      await initializeWithToken(
        freshGatewayConnection?.credentials?.token,
        isSandboxEnv
      );

      // Step 3: Set OAuth token if available
      if (freshGatewayConnection?.credentials?.oauth_token) {
        window.$PB.setOAuthToken(freshGatewayConnection.credentials.oauth_token);
      }


      // Step 5: Set card + customer + payment config
      setCardData({
        holder: cardholderName.trim(),
        number: cardNumber,
        expiration: expirationDate,
        cvv: cvv,
        save: 0,
      });

      let latestSession = null;

      try {
        latestSession = await fetchSession(sessionId);
      } catch (e) {
        console.warn("Failed to fetch session", e);
      }

      const customerName = `${formData?.firstName || ''} ${formData?.lastName || ''}`.trim() || `${latestSession?.customerData?.firstName || ''} ${latestSession?.customerData?.lastName || ''}`.trim() || cardholderName;
      setCustomerData({
        name: customerName,
        taxId: documentNumber,
        email: formData?.email || latestSession?.customerData?.email || '',
        phone: formData?.phone || latestSession?.customerData?.phone || '+5511999999999',
        street: formData?.billingAddress?.street || latestSession?.customerData?.billingAddress?.street || 'Av Paulista 1000',
        zip: formData?.billingAddress?.zipCode?.replace(/\D/g, '') || latestSession?.customerData?.billingAddress?.zipCode?.replace(/\D/g, '') || '01311100',
        city: formData?.billingAddress?.city || latestSession?.customerData?.billingAddress?.city || 'São Paulo',
        state: normalizeBrazilianStateCode(formData?.billingAddress?.state || latestSession?.customerData?.billingAddress?.state || 'SP'),
      });

      setPaymentConfig({ installments });

      // Step 6: Tokenize
      const cardToken = await tokenize();
      if (!cardToken) {
        throw new Error(t("paymentForms.card.tokenError") || "Failed to tokenize card");
      }

      setGatewayConnection(freshGatewayConnection);

      // Step 6: Process payment
      const payload = buildPayload(cardToken, freshGatewayConnection, latestSession);
      const res = await handleProcessPayment(payload, cardToken);
      const citUi = {
        isHostedPageMode,
        isEmbeddedCheckout,
        t,
        setError,
        setTransactionStatus,
        setShowStatusModal,
        onParentCallback,
        toast,
      };
      const { handled } = handleCitPaymentApiResponse(res, citUi);
      if (handled) return;

      if (res.status === 200) {
        if (res?.data?.hosted_redirect_page) {
          setHostedPageRedirect(res.data.hosted_redirect_page);
        }
        const responseRedirectUrl = res.data.redirect_url || checkoutConfDto?.checkoutRedirectUrl;
        setTransactionId(res?.data?.newTransactionId || res?.data?.transactionId);
        if (isEmbeddedCheckout) {
          onPaymentSuccess?.(res, latestSession);
          if (responseRedirectUrl) {
            onParentCallback?.({ type: "actionRequired", action: "REDIRECT", url: responseRedirectUrl });
            window.open(responseRedirectUrl, "_blank");
          } else {
            onParentCallback?.({ type: "success", transactionId: res.data.newTransactionId });
          }
        } else {
          setTransactionStatus("success");
          setShowStatusModal(true);
        }
      } else {
          applyCitDeclineOrErrorUi(
            {
              outcome: CIT_OUTCOME.ERROR,
              message:
                res?.data?.failureReason ||
                res?.data?.failureReasonLabel ||
                t("messages.error.somethingWrong")
            },
            citUi
          );

          return;
        }
    } catch (e) {
      console.error("Payment failed:", e);
      const citUi = {
        isHostedPageMode,
        isEmbeddedCheckout,
        t,
        setError,
        setTransactionStatus,
        setShowStatusModal,
        onParentCallback,
        toast,
      };
      if (e?.response && handleCitPaymentApiResponse(e.response, citUi).handled) return;
      applyCitDeclineOrErrorUi(
        { outcome: CIT_OUTCOME.ERROR, message: formatPaymentError(e?.message, t) },
        citUi
      );
      } finally {
      setLoading(false);
      if (!isEmbeddedCheckout) setProcessingModal(false);
    }
  }, [
    validateInputs, cardPagbrasilRouting, isEmbeddedCheckout, t,
    clearSdkError, prefetchedGatewayConnection, initializeWithToken,
    setCardData, cardholderName, cardNumber, expirationDate,
    cvv, setCustomerData, formData, documentNumber, setPaymentConfig,
    installments, tokenize, gatewayConnection, sessionId,
    buildPayload, handleProcessPayment, onPaymentSuccess, onParentCallback,
    setTransactionStatus
  ]);

  // Handle payment trigger from CheckoutContext
  useEffect(() => {
    if (
      triggerPaymentMethodEvent &&
      (isEmbeddedCheckout || (paymentMethodName === "card" && platformName === "pagbrasil"))
    ) {
      handlePayNow();
      resetTrigger();
    }
  }, [
    triggerPaymentMethodEvent,
    paymentMethodName,
    platformName,
    handlePayNow,
    resetTrigger,
    isEmbeddedCheckout,
  ]);

  // Listen websocket only for SDK version
  useWebSocket(isEmbeddedCheckout && transactionId, (event) => {
        const data = JSON.parse(event.data);

        if (!data?.status) return;

        if (data.status === "success") {
            onParentCallback?.({
                type: "success",
                transactionId,
            });
        }

        if (data.status === "failed") {
            onParentCallback?.({
                type: "failure",
            });
        }
    },
      undefined,
      undefined,
      (err) => {
          console.error("CARD WS error", err);
      }
  );

  return (
    <PagbrasilCardFormView
      t={t}
      siteName={siteName}
      // Form data
      cardholderName={cardholderName}
      cardNumber={cardNumber}
      expirationDate={expirationDate}
      cvv={cvv}
      documentNumber={documentNumber}
      installments={installments}
      // State
      loading={loading}
      error={error || documentError}
      cardError={sdkError}
      sdkLoading={sdkLoading}
      sdkReady={sdkReady}
      cardBrand={cardBrand}
      // Handlers
      onCardholderNameChange={handleCardholderNameChange}
      onCardNumberChange={handleCardNumberChange}
      onExpirationDateChange={handleExpirationDateChange}
      onCvvChange={handleCvvChange}
      onDocumentChange={handleDocumentChange}
      onInstallmentsChange={handleInstallmentsChange}
      // Styles
      inputStyles={inputStyles}
      labelStyles={labelStyles}
      // Modals
      showProcessingModal={showProcessingModal}
      setProcessingModal={setProcessingModal}
      showStatusModal={showStatusModal}
      setShowStatusModal={setShowStatusModal}
      transactionStatus={transactionStatus}
      checkoutConfDto={checkoutConfDto}
      transactionId={transactionId}
      hostedPageRedirect={hostedPageRedirect}
      // Options
      showInstallments={false} // Can be enabled if needed
      maxInstallments={12}
    />
  );
};

export default PagbrasilCardForm;
